import { useMemo, useState } from "react";
import { BlockLink } from "@/components/DrilldownLink";
import DataEntryKpiSchoolsModal from "@/components/DataEntryKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");
const fmtLakh = (v) => (typeof v === "number" ? `${(v / 100000).toFixed(1)}L` : "0L");

const DONUT_META = {
  "Data Completion": { variant: "", subtitle: "Very strong submission" },
  "School Certification": { variant: "orange", subtitle: "Highest priority gap" },
  "Data Accuracy": { variant: "blue", subtitle: "Validation needed" },
  "Retention Rate": { variant: "pink", subtitle: "Stable student retention" },
};

const blockOpsIndex = (block, districtAccuracy, districtDropout) => {
  if (block.ops_index != null) return block.ops_index;
  const completion = block.completion_rate ?? 0;
  const certification = block.certification_rate ?? 0;
  const accuracy = block.data_accuracy ?? districtAccuracy;
  const retention = 100 - (block.dropout_rate ?? districtDropout);
  return Math.round((completion * 0.3 + certification * 0.25 + accuracy * 0.25 + retention * 0.2) * 10) / 10;
};

const priorityTag = (index) => {
  if (index >= 78) return { label: "Good", className: "green" };
  if (index >= 72) return { label: "Medium", className: "amber" };
  return { label: "High", className: "red" };
};

const MetricDonut = ({ value, display, label, subtitle, variant = "" }) => (
  <div className="ed-ops-metric-donut">
    <div className={`ed-ops-donut ${variant}`} style={{ "--pct": `${Math.min(Math.max(value, 0), 100)}%` }}>
      <strong>{display ?? pct(value, value >= 99 ? 2 : 1)}</strong>
    </div>
    <b>{label}</b>
    <span>{subtitle}</span>
  </div>
);

const KpiCard = ({ label, value, hint, iconVariant, children }) => (
  <article className="ed-ops-kpi-card">
    <div className="ed-ops-kpi-top">
      <small>{label}</small>
      <span className={`ed-ops-mini-icon ${iconVariant || ""}`}>{children}</span>
    </div>
    <strong>{value}</strong>
    <span>{hint}</span>
  </article>
);

const ExecutiveOperationalTab = ({ operationalData, scopeLabel }) => {
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [kpiModalMetric, setKpiModalMetric] = useState(null);

  const summary = operationalData?.summary ?? {};
  const dataEntry = operationalData?.data_entry_metrics ?? {};
  const dropbox = operationalData?.dropbox_metrics ?? {};
  const enrolment = operationalData?.enrolment_metrics ?? {};
  const breakdown = operationalData?.index_breakdown ?? [];
  const blocks = operationalData?.block_performance ?? [];

  const opsIndex = summary.operational_index ?? 0;
  const totalStudents = summary.total_students ?? 0;
  const completionRate = dataEntry.completion_rate ?? 0;
  const certificationRate = dataEntry.certification_rate ?? 0;
  const dataAccuracy = dropbox.data_accuracy ?? 0;
  const dropoutRate = dropbox.dropout_rate ?? 0;
  const retentionRate = Math.round((100 - dropoutRate) * 100) / 100;

  const boysEnrol = enrolment.boys_enrolment ?? 0;
  const girlsEnrol = enrolment.girls_enrolment ?? 0;
  const totalEnrol = boysEnrol + girlsEnrol || enrolment.total_enrolment || 0;
  const boysPct = totalEnrol > 0 ? Math.round((boysEnrol / totalEnrol) * 1000) / 10 : enrolment.boys_pct ?? 52;
  const girlsPct = totalEnrol > 0 ? Math.round((girlsEnrol / totalEnrol) * 1000) / 10 : enrolment.girls_pct ?? 48;

  const genderSkew = Math.abs(boysPct - girlsPct);
  const genderNarrative =
    genderSkew <= 2
      ? "Student distribution is well balanced across boys and girls."
      : boysPct > girlsPct
        ? `Enrolment skews slightly towards boys (${boysPct}% vs ${girlsPct}% girls).`
        : `Enrolment skews slightly towards girls (${girlsPct}% vs ${boysPct}% boys).`;

  const opsStatusHint = opsIndex >= 78 ? "Good status" : opsIndex >= 72 ? "Amber status" : "Needs attention";

  const tableRows = useMemo(() => {
    const rows = blocks.map((block) => {
      const idx = blockOpsIndex(block, dataAccuracy, dropoutRate);
      const boys = block.boys_pct ?? boysPct;
      const girls = block.girls_pct ?? girlsPct;
      return {
        ...block,
        students: block.students ?? 0,
        data_accuracy: block.data_accuracy ?? dataAccuracy,
        boys_pct: boys,
        girls_pct: girls,
        ops_index: idx,
        priority: priorityTag(idx),
      };
    });
    const sorted = rows.sort((a, b) => b.ops_index - a.ops_index);
    if (priorityOnly) {
      return sorted.filter((r) => r.priority.className === "red");
    }
    return sorted;
  }, [blocks, dataAccuracy, dropoutRate, boysPct, girlsPct, priorityOnly]);

  const heroNarrative =
    completionRate >= 99 && certificationRate < 15
      ? "Data completion is near perfect, but certification remains the key bottleneck. Data accuracy and gender balance should be monitored block-wise."
      : completionRate >= 90
        ? "Operational submission is strong district-wide with certification and validation workflows needing focused follow-up."
        : "Operational readiness needs improvement across data completion, certification and accuracy indicators.";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ed-content ed-ops-view" data-testid="executive-operational-tab">
      <section className="ed-hero-card ed-ops-hero">
        <div className="ed-ops-layout">
          <div className="ed-ops-summary">
            <div
              className="ed-ops-gauge"
              aria-label={`Operational index ${opsIndex}`}
              style={{
                background: `radial-gradient(circle at center, white 0 53%, transparent 54%), conic-gradient(var(--ed-secondary, #ff8a1f) 0 ${opsIndex}%, rgba(255,138,31,.11) ${opsIndex}% 100%)`,
              }}
            >
              <div>
                <strong>{opsIndex}</strong>
                <span>Ops</span>
              </div>
            </div>
            <div className="ed-health-copy">
              <h2>Operational Overview</h2>
              <p>{heroNarrative}</p>
              <div className="ed-status-row">
                {completionRate >= 95 && (
                  <HeroStatusChip chipClass="ed-status-chip" variant="green">
                    {pct(completionRate, 2)} Data Completion
                  </HeroStatusChip>
                )}
                {certificationRate < 20 && (
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant="amber"
                    onClick={() => setKpiModalMetric("non_certified")}
                    ariaLabel="Certification gap — view non-certified schools"
                  >
                    Certification Gap
                  </HeroStatusChip>
                )}
                <HeroStatusChip
                  chipClass="ed-status-chip"
                  variant="pink"
                  onClick={() => scrollTo("ed-ops-table")}
                  ariaLabel="Gender enrolment split — view block-level breakdown"
                >
                  Girls {Math.round(girlsPct)}% · Boys {Math.round(boysPct)}%
                </HeroStatusChip>
              </div>
            </div>
          </div>

          <div className="ed-ops-kpi-grid" aria-label="Operational KPI cards">
            <KpiCard label="Total Students" value={fmtLakh(totalStudents)} hint="Student base">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                <circle cx="12" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              </svg>
            </KpiCard>
            <KpiCard label="Completion Rate" value={pct(completionRate, 2)} hint="Data submitted" iconVariant="green">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </KpiCard>
            <KpiCard label="Certification" value={pct(certificationRate)} hint="Critical lag" iconVariant="purple">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
              </svg>
            </KpiCard>
            <KpiCard label="Data Accuracy" value={pct(dataAccuracy)} hint="Needs validation" iconVariant="cyan">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3v18h18" />
                <path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </KpiCard>
            <KpiCard label="Retention Rate" value={pct(retentionRate, 2)} hint="Strong continuity" iconVariant="pink">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                <path d="M21 3v6h-6" />
              </svg>
            </KpiCard>
            <KpiCard label="Ops Index" value={opsIndex} hint={opsStatusHint} iconVariant="orange">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              </svg>
            </KpiCard>
          </div>
        </div>
      </section>

      <div className="ed-section-title">
        <div>
          <h2>Operational Diagnostics</h2>
          <p>
            Completion, certification, accuracy and retention are separated into clear diagnostic cards with gender balance
            shown as a focused decision panel.
          </p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-ops-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ed-ops-diagnostic-grid">
        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Operational Index Breakdown</h3>
              <p>Core indicators driving the district operational index.</p>
            </div>
            <span className="ed-pill">Current month</span>
          </div>
          <div className="ed-ops-gauge-grid">
            {breakdown.length ? (
              breakdown.map((item) => (
                <MetricDonut
                  key={item.metric}
                  value={item.value}
                  label={item.metric}
                  subtitle={DONUT_META[item.metric]?.subtitle || ""}
                  variant={DONUT_META[item.metric]?.variant || ""}
                  display={pct(item.value, item.metric === "Data Completion" || item.metric === "Retention Rate" ? 2 : 1)}
                />
              ))
            ) : (
              <>
                <MetricDonut value={completionRate} label="Data Completion" subtitle="Very strong submission" display={pct(completionRate, 2)} />
                <MetricDonut value={certificationRate} label="School Certification" subtitle="Highest priority gap" variant="orange" />
                <MetricDonut value={dataAccuracy} label="Data Accuracy" subtitle="Validation needed" variant="blue" />
                <MetricDonut value={retentionRate} label="Retention Rate" subtitle="Stable student retention" variant="pink" display={pct(retentionRate, 2)} />
              </>
            )}
          </div>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Enrolment and Gender Balance</h3>
              <p>{genderNarrative}</p>
            </div>
            <span className="ed-pill">{fmtLakh(totalStudents || totalEnrol)} students</span>
          </div>
          <div className="ed-ops-gender-card">
            <div className="ed-ops-gender-donut-wrap">
              <div
                className="ed-ops-gender-donut"
                style={{
                  background: `radial-gradient(circle at center, #fff 0 55%, transparent 56%), conic-gradient(var(--ed-primary) 0 ${boysPct}%, var(--ed-pink, #ec4899) ${boysPct}% 100%)`,
                }}
              >
                <strong>100%</strong>
              </div>
              <span className="ed-ops-gender-badge boys">Boys {boysPct}%</span>
              <span className="ed-ops-gender-badge girls">Girls {girlsPct}%</span>
            </div>
            <div className="ed-ops-gender-copy">
              <div className="ed-ops-gender-stat">
                <b>Boys · {boysPct}%</b>
                <span>Slightly higher share of total enrolment. Monitor dropout and retention at block level.</span>
              </div>
              <div className="ed-ops-gender-stat">
                <b>Girls · {girlsPct}%</b>
                <span>Near-parity but still needs periodic review across blocks and school categories.</span>
              </div>
              <div className="ed-ops-gender-stat">
                <b>Gender action</b>
                <span>Use block-level alerts where girls&apos; share falls below district average.</span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <div className="ed-section-title" id="ed-ops-actions">
        <div>
          <h2>Insights and Next Actions</h2>
          <p>
            Operational signals are converted into clear actions so officers can move from monitoring to certification,
            validation and correction.
          </p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-ops-table")}>
          View block table →
        </button>
      </div>

      <section className="ed-ops-lower-grid">
        <article className="ed-ops-insights-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Operational Signals</h3>
              <p>Summarized from completion, certification, Dropbox accuracy, enrolment split and retention indicators.</p>
            </div>
            <span className="ed-pill">Rule-based</span>
          </div>
          <div className="ed-ops-insight-stack">
            <article className="ed-ops-insight-card">
              <span className="ed-icon-box green">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 17 6-6 4 4 8-8" />
                  <path d="M14 7h7v7" />
                </svg>
              </span>
              <div>
                <h4>Data completion is almost universal</h4>
                <p>
                  {fmt(totalStudents)} students are covered and operational data completion is {pct(completionRate, 2)},
                  indicating strong submission discipline.
                </p>
              </div>
              <span className="ed-ops-priority good">Strong</span>
            </article>
            <article className="ed-ops-insight-card">
              <span className="ed-icon-box orange">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 21h8" />
                  <path d="M12 17v4" />
                  <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                </svg>
              </span>
              <div>
                <h4>Certification is the main bottleneck</h4>
                <p>
                  Only {pct(certificationRate)} schools are certified despite high completion, showing that review and
                  approval workflow is lagging.
                </p>
              </div>
              <span className="ed-ops-priority high">Critical</span>
            </article>
            <article className="ed-ops-insight-card">
              <span className="ed-icon-box cyan">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
              </span>
              <div>
                <h4>Data accuracy needs validation sprint</h4>
                <p>
                  Dropbox data accuracy is {pct(dataAccuracy)}, so random audits and school-level corrections should be
                  scheduled.
                </p>
              </div>
              <span className="ed-ops-priority">Medium</span>
            </article>
            <article className="ed-ops-insight-card">
              <span className="ed-icon-box pink">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </span>
              <div>
                <h4>Retention and gender balance are stable</h4>
                <p>
                  Retention is {pct(retentionRate, 2)} and enrolment split is {Math.round(boysPct)}% boys and{" "}
                  {Math.round(girlsPct)}% girls, but gender parity should be monitored by block.
                </p>
              </div>
              <span className="ed-ops-priority good">Stable</span>
            </article>
          </div>
        </article>

        <aside className="ed-ops-action-panel">
          <h3>Recommended Action Queue</h3>
          <div className="ed-action-card">
            <article className="ed-action-item">
              <span className="ed-ops-small-icon red">1</span>
              <div>
                <h4>Clear certification backlog</h4>
                <p>Prioritize schools that have completed data entry but are pending officer certification.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-ops-small-icon orange">2</span>
              <div>
                <h4>Run Dropbox accuracy validation</h4>
                <p>Target blocks below {pct(dataAccuracy)} accuracy and create school-wise correction tasks.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-ops-small-icon green">3</span>
              <div>
                <h4>Monitor gender parity exceptions</h4>
                <p>Generate alerts for blocks where girls&apos; enrolment falls below district baseline.</p>
              </div>
            </article>
          </div>
          <button type="button" className="ed-ops-learn-more" onClick={() => scrollTo("ed-ops-table")}>
            <span>Open operational action tracker</span>
            <span>→</span>
          </button>
        </aside>
      </section>

      <section className="ed-panel ed-table-card" id="ed-ops-table">
        <div className="ed-table-head">
          <div>
            <h3>Block-Wise Operational Summary</h3>
            <p>Detailed block table for completion, certification, data accuracy, gender balance and intervention priority.</p>
          </div>
          <div className="ed-map-table-tools">
            <button
              type="button"
              className="ed-map-filter-chip"
              onClick={() => setPriorityOnly((v) => !v)}
            >
              {priorityOnly ? "Show all ▾" : "High priority only ▾"}
            </button>
          </div>
        </div>
        <table className="ed-table" aria-label="Block-wise operational summary">
          <thead>
            <tr>
              <th>Block</th>
              <th>Students</th>
              <th>Completion</th>
              <th>Certification</th>
              <th>Data Accuracy</th>
              <th>Boys</th>
              <th>Girls</th>
              <th>Ops Index</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? (
              tableRows.map((row) => (
                <tr key={row.block_code || row.block_name}>
                  <td>
                    {row.block_code ? (
                      <BlockLink blockCode={row.block_code}>{row.block_name}</BlockLink>
                    ) : (
                      row.block_name
                    )}
                  </td>
                  <td>{fmt(row.students)}</td>
                  <td>{pct(row.completion_rate, row.completion_rate >= 99.9 ? 2 : 1)}</td>
                  <td>{pct(row.certification_rate)}</td>
                  <td>{pct(row.data_accuracy)}</td>
                  <td>{Math.round(row.boys_pct)}%</td>
                  <td>{Math.round(row.girls_pct)}%</td>
                  <td className="ed-score">{row.ops_index}</td>
                  <td>
                    <span className={`ed-tag ${row.priority.className}`}>{row.priority.label}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9}>No block operational data available for {scopeLabel}.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <DataEntryKpiSchoolsModal
        open={Boolean(kpiModalMetric)}
        metric={kpiModalMetric}
        onClose={() => setKpiModalMetric(null)}
        scopeLabel={scopeLabel}
        districtPct={kpiModalMetric === "non_certified" ? certificationRate : null}
      />
    </div>
  );
};

export default ExecutiveOperationalTab;
